import React from 'react';
import { Calculator, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { PenaltyResponse } from '../types';

function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const [y, m, d] = value.split('-');
  if (!y || !m || !d) return value;
  return `${d}.${m}.${y}`;
}

export default function PenaltyResults({ results }: { results: PenaltyResponse }) {
  if (!results.found) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Недостаточно данных для расчёта</h3>
            <p className="text-sm text-gray-600">{results.notes || 'Не удалось определить сумму долга и дату начала просрочки.'}</p>
            {results.amount != null && <p className="text-sm text-gray-500 mt-2">Найденная сумма: {formatMoney(results.amount)}</p>}
            {results.debt_start_date && <p className="text-sm text-gray-500">Дата начала просрочки: {formatDate(results.debt_start_date)}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {results.warning && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{results.warning}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calculator className="w-5 h-5" /> Неустойка по ст. 395 ГК РФ
          </h2>
          <p className="text-white/80 text-sm truncate">{results.filename}</p>
        </div>

        <div className="p-5">
          <div className="text-center mb-5">
            <p className="text-sm text-gray-500">Сумма процентов на {formatDate(results.debt_end_date)}</p>
            <p className="text-4xl font-bold text-rose-600 mt-1">{formatMoney(results.total)}</p>
            <p className="text-sm text-gray-500 mt-1">
              за {results.total_days - (results.uncovered_days || 0)} дн. начисления
              {results.uncovered_days ? ` (+${results.uncovered_days} дн. без известной ставки)` : ''}
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mb-2">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Сумма долга</p>
              <p className="font-semibold text-gray-800">{formatMoney(results.amount)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Начало просрочки</p>
              <p className="font-semibold text-gray-800">{formatDate(results.debt_start_date)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Расчёт по дату</p>
              <p className="font-semibold text-gray-800">{formatDate(results.debt_end_date)}</p>
            </div>
          </div>

          {results.obligation && (
            <p className="text-sm text-gray-600 mt-2"><span className="text-gray-400">Обязательство:</span> {results.obligation}</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Период</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Дней</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Ставка ЦБ</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Дней в году</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Проценты</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {results.periods.map((p, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-800">{formatDate(p.start)} — {formatDate(p.end)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{p.days}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{p.rate}%</td>
                  <td className="px-4 py-2 text-right text-gray-600">{p.days_in_year}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-800">{formatMoney(p.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-gray-200 bg-gray-50">
              <tr>
                <td className="px-4 py-2 font-semibold text-gray-800" colSpan={4}>Итого</td>
                <td className="px-4 py-2 text-right font-bold text-rose-600">{formatMoney(results.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {results.notes && (
        <div className="flex items-start gap-2 text-gray-600 bg-gray-50 rounded-lg p-3 text-sm">
          <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
          <p>{results.notes}</p>
        </div>
      )}

      {results.rates_as_of && (
        <p className="text-xs text-gray-400 px-1">
          Ключевая ставка ЦБ РФ актуальна на {formatDate(results.rates_as_of)}
          {results.rates_source === 'live' ? ' (получена онлайн с cbr.ru)' : ' (локальная таблица)'}.
        </p>
      )}

      {results.disclaimer && (
        <p className="text-xs text-gray-400 px-1">{results.disclaimer}</p>
      )}
    </div>
  );
}
